import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ────────────────────────────────────────────────────────────────────
// CONFIGURATION — paste your ad unit slot IDs from the AdSense dashboard
// after creating them (Ads → By ad unit → Create new ad unit).
// ────────────────────────────────────────────────────────────────────
const ADSENSE_PUB_ID = 'ca-pub-7516955484959844';
const LOW_AD_REVIEW_MODE = process.env.REACT_APP_LOW_AD_REVIEW_MODE === 'true';
const AD_SLOTS = {
  blog:   '2565267399',
  footer: '5247790571',
};

// ── Upsell banner (between sections on Home / CapsuleList) ──────────
// Shown to free users. Lighter version for starter users.
export function UpgradeNudge({ context = 'home' }) {
  const { plan } = useAuth();

  // Legacy users never see upsells
  if (plan === 'legacy') return null;

  const isFree = plan !== 'starter';

  if (context === 'home') {
    return (
      <section className="px-6 py-8 bg-gradient-to-r from-vault-900 via-vault-800 to-vault-900">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="text-white font-semibold text-sm">
              {isFree
                ? '🚀 Unlock more capsules, bigger files, and family trees'
                : '⭐ Go unlimited with Legacy'}
            </p>
            <p className="text-vault-400 text-xs mt-1">
              {isFree
                ? 'Starter plans start at $25/year. Protect what matters most.'
                : 'Unlimited capsules, 30 MB files, and priority support for $50/year.'}
            </p>
          </div>
          <Link
            to="/pricing"
            className="flex-shrink-0 text-xs font-bold bg-accent-500 hover:bg-accent-600 text-white px-5 py-2.5 rounded-lg transition-colors shadow-sm"
          >
            {isFree ? 'See Plans' : 'Upgrade to Legacy'}
          </Link>
        </div>
      </section>
    );
  }

  // CapsuleList context — more compact
  if (context === 'capsule-list') {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-accent-300 bg-accent-50/40 p-5 text-center">
        <p className="text-sm font-semibold text-vault-800">
          {isFree ? '💡 Want more capsules?' : '💡 Need unlimited capsules?'}
        </p>
        <p className="text-xs text-vault-500 mt-1 mb-3 max-w-md mx-auto">
          {isFree
            ? 'Upgrade to create up to 3 capsules, add more contacts, and unlock family tree templates.'
            : 'Legacy gives you unlimited capsules, 30 MB file uploads, and 4 files per capsule.'}
        </p>
        <Link
          to="/pricing"
          className="inline-block text-xs font-semibold text-accent-600 hover:text-accent-700 underline underline-offset-2"
        >
          Compare plans →
        </Link>
      </div>
    );
  }

  return null;
}

// ── Google AdSense display ad ───────────────────────────────────────
// Renders a responsive AdSense unit. Hidden for paid users.
// Won't render anything until you replace the placeholder pub ID above.
export function GoogleAd({ slot = 'blog', format = 'auto', className = '' }) {
  const { plan } = useAuth();
  const adRef = useRef(null);
  const containerRef = useRef(null);
  const pushed = useRef(false);
  const [showFallback, setShowFallback] = useState(false);

  const isBlogSlot = slot === 'blog';
  const isPaid = plan === 'starter' || plan === 'legacy';
  const isPlaceholder = ADSENSE_PUB_ID.includes('XXXX');
  const isPaidBlocked = !isBlogSlot && isPaid;
  const adsDisabled = LOW_AD_REVIEW_MODE && !isBlogSlot;

  useEffect(() => {
    if (isPaidBlocked || isPlaceholder || adsDisabled || pushed.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // AdSense can fail when scripts are blocked; show a fallback on blog placements.
      if (isBlogSlot) setShowFallback(true);
    }

    // If an ad is blocked or does not fill, show fallback on blog placements.
    const timer = setTimeout(() => {
      if (containerRef.current && adRef.current) {
        const filled = adRef.current.getAttribute('data-ad-status') === 'filled'
          || adRef.current.offsetHeight > 0;
        if (!filled) {
          if (isBlogSlot) {
            setShowFallback(true);
          } else {
            containerRef.current.style.display = 'none';
          }
        }
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [isPaidBlocked, isPlaceholder, adsDisabled, isBlogSlot]);

  // Keep account-based ad hiding for non-blog slots.
  if (isPaidBlocked || isPlaceholder || adsDisabled) return null;

  const adSlotId = AD_SLOTS[slot] || slot;

  return (
    <div ref={containerRef} className={`overflow-hidden ${className}`}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_PUB_ID}
        data-ad-slot={adSlotId}
        data-ad-format={format}
        data-full-width-responsive="true"
        ref={adRef}
      />
      {isBlogSlot && showFallback && (
        <div className="mt-3 rounded-xl border border-vault-200 bg-vault-50 p-4 text-left">
          <p className="text-[11px] uppercase tracking-wider text-vault-500 font-semibold mb-1">Sponsor</p>
          <p className="text-sm font-semibold text-vault-900">Support LegaxyVault</p>
          <p className="text-xs text-vault-600 mt-1">
            Ads may be blocked or unavailable right now. You can still support us by checking plans.
          </p>
          <Link
            to="/pricing"
            className="inline-block mt-3 text-xs font-semibold text-accent-600 hover:text-accent-700"
          >
            View pricing →
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Footer ad banner (replaces old partner links) ───────────────────
// A single horizontal AdSense unit above the footer.
export function FooterAd() {
  const { plan } = useAuth();

  // No ads for paid users
  if (plan === 'starter' || plan === 'legacy') return null;
  if (LOW_AD_REVIEW_MODE) return null;

  // Don't render while using placeholder ID
  if (ADSENSE_PUB_ID.includes('XXXX')) return null;

  return (
    <div className="bg-vault-50 border-t border-vault-200">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <GoogleAd slot="footer" format="horizontal" />
      </div>
    </div>
  );
}
